

import React, { useState, useEffect } from "react";
import DetailedUserView from "./DetailedUserView";
import { isAuthenticated } from "../auth/helper";
import {
  generateReportApi,
  updateTimesheetProcessData,
  getAllTimesheetsByFilter,
  getAllUsers,
  getJobTimesheetSummary,
} from "./helper/coreapicalls";
import Pagination from "./Pagination";
import TimesheetSearch from "./TimesheetSearch";
import "./AdminApprovalDashboard.css";

const ADVANCED_REPORT_ALLOWED_EMAILS = [
    "admin@mastersystems.com",
    "jeevan@mastersystems.com",
    "thomas@mastersystems.com",
    "testacc@test.com",
    "jilby2@mastersystems.com"


    // Add more permitted email IDs here
  ];

const AdminApprovalControl = (onClosePopup) => {
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [timesheetsData, setTimesheetsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [jobSummary, setJobSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [engineerOptions, setEngineerOptions] = useState([]);
  const [showInactiveEngineers, setShowInactiveEngineers] = useState(false);
  const [searchPreset, setSearchPreset] = useState(null);
  const [reportVersion, setReportVersion] = useState("standard");
  const [jobCopied, setJobCopied] = useState(false);
  const [pendingCopied, setPendingCopied] = useState(false);

  const authData = isAuthenticated();

  const loggedInEmail = (
    authData?.user?.emp_email ||
    authData?.emp_email ||
    ""
  )
    .trim()
    .toLowerCase();

  const canUseAdvancedReport =
    ADVANCED_REPORT_ALLOWED_EMAILS.includes(loggedInEmail);

  const loadingMessage = () => {
    return (
      loading && (
        <div className="overlay">
          <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border text-danger" role="status"></div>
          </div>
        </div>
      )
    );
  };

  const loadEngineers = async (includeInactive = false) => {
    try {
      const users = await getAllUsers(
        !includeInactive,  // activeOnly
        true,              // engineerDropdown
        includeInactive    // includeInactive
      );

      const userList = Array.isArray(users)
        ? users
        : users.results
        ? users.results
        : [];

      const sortedUsers = [...userList].sort((a, b) => {
        // Active engineers first
        if (a.is_active !== b.is_active) {
          return a.is_active ? -1 : 1;
        }

        return (a.emp_name || "").localeCompare(b.emp_name || "");
      });

      const engineers = sortedUsers.map((u) => ({
        value: u.emp_name,
        label:
          u.is_active === false
            ? `${u.emp_name} (Inactive)`
            : u.emp_name,
      }));

      setEngineerOptions([
        { value: "", label: "All Engineers" },
        ...engineers,
      ]);
    } catch (error) {
      console.error("Error loading engineers:", error);
    }
  };

  useEffect(() => {
    fetchTimesheets(currentPage, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    loadEngineers(showInactiveEngineers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactiveEngineers]);



  const handleViewClick = (timesheet) => {
    setSelectedTimesheet(timesheet);
  };

  const getWeekendDay = (dateString) => {
    if (!dateString) return "";

    // Parse YYYY-MM-DD as a local date to avoid timezone date shifts.
    const parts = String(dateString).split("-").map(Number);

    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return "";
    }

    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    const dayNumber = date.getDay();

    if (dayNumber === 6) return "Saturday";
    if (dayNumber === 0) return "Sunday";

    return "";
  };

  function decimalToTime(decimalHours) {
    if (
      decimalHours === null ||
      decimalHours === undefined ||
      decimalHours === ""
    ) {
      return "-";
    }

    const value = Number(decimalHours);

    if (Number.isNaN(value)) {
      return "-";
    }

    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);

    return hours + ":" + (minutes < 10 ? "0" : "") + minutes;
  }

  function formatDateTime(dateTimeValue) {
    if (!dateTimeValue) {
      return "-";
    }

    const date = new Date(dateTimeValue);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString();
  }

  const handleProcessClick = async (timesheet) => {
    try {
      setLoading(true);

      const newProcessStatus = !timesheet.timesheet_final_process_status;

      const jsonData = {
        timesheet_final_process_status: newProcessStatus,
      };

      const updatedTimesheet = await updateTimesheetProcessData(
        timesheet.timesheet_id,
        jsonData
      );

        setTimesheetsData((prevData) =>
          prevData.map((item) =>
            item.timesheet_id === timesheet.timesheet_id
              ? {
                  ...item,

                  timesheet_final_process_status:
                    updatedTimesheet.timesheet_final_process_status,

                  timesheet_processed_by:
                    updatedTimesheet.timesheet_processed_by,

                  timesheet_processed_by_name:
                    updatedTimesheet.timesheet_processed_by_name,

                  timesheet_processed_at:
                    updatedTimesheet.timesheet_processed_at,
                }
              : item
          )
        );
        if (filters.Job_No) {
          await loadJobSummary(filters.Job_No);
        }
    } catch (error) {
      console.error("Error updating timesheet:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = (editedTimesheet) => {
    setTimesheetsData((prevData) =>
      prevData.map((timesheet) =>
        timesheet.timesheet_id === editedTimesheet.timesheet_id
          ? editedTimesheet
          : timesheet
      )
    );
    setSelectedTimesheet(null);
  };

  const handleCloseEdit = () => {
    setSelectedTimesheet(null);
  };

  const handleSearch = async (searchFilters) => {
    setFilters(searchFilters);
    setCurrentPage(1);
    await fetchTimesheets(1, searchFilters);
  };

  const handleReportGen = async (searchFilters) => {
    setFilters(searchFilters);
    setCurrentPage(1);
    await generateReport(1, searchFilters);
  };

  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    await fetchTimesheets(newPage, filters);
  };

  const handleJobQuickSearch = async (jobNo) => {
    if (!jobNo) return;

    const newFilters = {
      ...filters,
      Job_No: jobNo,
    };

    setSearchPreset(newFilters);
    setFilters(newFilters);
    setCurrentPage(1);

    await fetchTimesheets(1, newFilters);
  };

  const loadJobSummary = async (jobNumber) => {
  const cleanedJobNumber = String(jobNumber || "").trim();

    if (!cleanedJobNumber) {
      setJobSummary(null);
      return;
    }

    try {
      const summary = await getJobTimesheetSummary(
        cleanedJobNumber
      );

      setJobSummary(summary);
    } catch (error) {
      console.error(
        "Error loading job summary:",
        error
      );

      setJobSummary(null);
    }
  };

  const fetchTimesheets = async (
    page = 1,
    searchFilters = filters,
    approved = true
  ) => {
    try {
      setLoading(true);

      const result = await getAllTimesheetsByFilter(
        searchFilters.teamlead === true ? searchFilters.teamlead : null,
        searchFilters.manager === true ? searchFilters.manager : null,
        searchFilters.sr === true ? searchFilters.sr : null,
        searchFilters.teamId && searchFilters.teamId.team_id
          ? searchFilters.teamId.team_id
          : null,
        searchFilters.branchId && searchFilters.branchId.branch_id
          ? searchFilters.branchId.branch_id
          : null,
        page,
        searchFilters.msid,
        searchFilters.eng_name,
        searchFilters.vessel_name,
        searchFilters.startdate,
        searchFilters.enddate,
        searchFilters.Job_No,
        searchFilters.OT === true ? searchFilters.OT : null,
        approved,
        searchFilters.process_status
      );

      setTimesheetsData(result.timesheetsData);
      setTotalCount(result.count);
      setTotalPages(Math.ceil(result.count / 100));

      await loadJobSummary(searchFilters.Job_No);
    } catch (error) {
      console.error("Error fetching timesheet data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (
    page = 1,
    searchFilters = filters,
    approved = true
  ) => {
    try {
      setLoading(true);

      const result = await generateReportApi(
        searchFilters.teamlead === true ? searchFilters.teamlead : null,
        searchFilters.manager === true ? searchFilters.manager : null,
        searchFilters.sr === true ? searchFilters.sr : null,
        searchFilters.teamId && searchFilters.teamId.team_id
          ? searchFilters.teamId.team_id
          : null,
        searchFilters.branchId && searchFilters.branchId.branch_id
          ? searchFilters.branchId.branch_id
          : null,
        page,
        searchFilters.msid,
        searchFilters.eng_name,
        searchFilters.vessel_name,
        searchFilters.startdate,
        searchFilters.enddate,
        searchFilters.Job_No,
        searchFilters.OT === true ? searchFilters.OT : null,
        approved,
        searchFilters.process_status,
        canUseAdvancedReport ? reportVersion : "standard"
      );

      downloadFileFromResult(result);
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadFileFromResult = ({ blob, headers }) => {
    const fileBlob = new Blob([blob], { type: headers.get("content-type") });
    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = headers.get("content-disposition");
    let fileName = "downloaded_file.xlsx";

    if (contentDisposition && contentDisposition.includes("filename=")) {
      fileName = contentDisposition
        .split("filename=")[1]
        .split(";")[0]
        .replace(/"/g, "");
    }

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const normalUserfilterConfig = [
    { type: "text", name: "msid", label: "MSID" },

    {
      type: "customSelect",
      name: "eng_name",
      label: "Engineer",
      options: engineerOptions,
    },

    { type: "text", name: "vessel_name", label: "Vessel Name" },
    { type: "text", name: "Job_No", label: "Job No." },

    { type: "date", name: "startdate", label: "Start Date" },
    { type: "date", name: "enddate", label: "End Date" },

    { type: "select", name: "Team", label: "Team" },
    { type: "select", name: "Branch", label: "Branch" },

    {
      type: "customSelect",
      name: "process_status",
      label: "Process",
      options: [
        { value: "all", label: "All" },
        { value: "completed", label: "Completed" },
        { value: "not_completed", label: "Pending" },
      ],
    },

    { type: "checkbox", name: "OT", label: "Overtime" },
  ];

  // Explicit column widths keep header and body perfectly aligned.
  const columnStyles = {
    view: { minWidth: 70, width: 70, maxWidth: 70 },
    date: { minWidth: 112, width: 112, maxWidth: 112 },
    jobNo: { minWidth: 85, width: 85, maxWidth: 85 },
    database: { minWidth: 80, width: 80, maxWidth: 80 },
    vessel: { minWidth: 130, width: 130, maxWidth: 130 },
    engineer: { minWidth: 95, width: 95, maxWidth: 95 },
    time: { minWidth: 72, width: 72, maxWidth: 72 },
    normalHours: { minWidth: 64, width: 64, maxWidth: 64 },
    hours: { minWidth: 58, width: 58, maxWidth: 58 },
    amountSmall: { minWidth: 58, width: 58, maxWidth: 58 },
    amount: { minWidth: 68, width: 68, maxWidth: 68 },
    flag: { minWidth: 56, width: 56, maxWidth: 56 },
    approval: { minWidth: 68, width: 68, maxWidth: 68 },
    finalStatus: { minWidth: 78, width: 78, maxWidth: 78 },
    processStatus: { minWidth: 84, width: 84, maxWidth: 84 },
    processedBy: { minWidth: 105, width: 105, maxWidth: 105 },
    processedAt: { minWidth: 145, width: 145, maxWidth: 145 },
    comments: { minWidth: 150, width: 150, maxWidth: 150 },
  };

  const commonCellStyle = {
    textAlign: "center",
    verticalAlign: "middle",
    padding: "6px 4px",
  };

  const stickyProcessHeaderStyle = {
    ...columnStyles.processStatus,
    position: "sticky",
    right: 0,
    zIndex: 5,
    backgroundColor: "white",
    boxShadow: "-2px 0 4px rgba(0,0,0,0.15)",
  };

  const stickyTotalHeaderStyle = {
    ...columnStyles.amountSmall,
    position: "sticky",
    right: columnStyles.processStatus.width,
    zIndex: 5,
    backgroundColor: "white",
    boxShadow: "-2px 0 4px rgba(0,0,0,0.08)",
  };

  const stickyProcessCellStyle = {
    ...commonCellStyle,
    ...columnStyles.processStatus,
    position: "sticky",
    right: 0,
    zIndex: 2,
    boxShadow: "-2px 0 4px rgba(0,0,0,0.15)",
  };

  const stickyTotalCellStyle = {
    ...commonCellStyle,
    ...columnStyles.amountSmall,
    position: "sticky",
    right: columnStyles.processStatus.width,
    zIndex: 2,
    boxShadow: "-2px 0 4px rgba(0,0,0,0.08)",
  };
  
  const processStatusButtonStyle = {
    width: 72,
    height: 28,
    padding: "2px 4px",
    margin: 0,
    fontSize: 10,
  };

  const copyToClipboard = async (text) => {
    try {
      // Modern browsers
      if (
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // Mobile / older-browser fallback
      const textArea = document.createElement("textarea");

      textArea.value = text;

      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      textArea.style.opacity = "0";

      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");

      document.body.removeChild(textArea);

      return successful;

    } catch (error) {
      console.error("Copy failed:", error);
      return false;
    }
  };

  return (
    <div className="admin-approval-control">
      {loading && loadingMessage()}

      {/* Keep Process filter control visually consistent with other search inputs. */}
        <style>{`


          /* Keep complete table header fixed when scrolling vertically */
          .admin-timesheet-table thead th {
            position: sticky !important;
            top: 0 !important;
            z-index: 20 !important;
            background: white !important;
          }

          .admin-force-black,
          .admin-force-black span,
          .admin-force-black label,
          .admin-force-black div {
            color: #000000 !important;
          }

          .admin-approval-search,
          .admin-approval-search span,
          .admin-approval-search label {
            color: #000000 !important;
          }
        `}</style>

        <section className="admin-filter-card">

          {/* TOP SECTION */}
          <div className="admin-filter-top">

            {/* ENGINEER STATUS */}
            <div>
              <span className="admin-filter-heading">
                Engineers
              </span>

              <div className="admin-engineer-radios">

                <label className="admin-engineer-option">
                  <input
                    type="radio"
                    name="engineer_status"
                    checked={!showInactiveEngineers}
                    onChange={() =>
                      setShowInactiveEngineers(false)
                    }
                  />

                  Active Only
                </label>

                <label className="admin-engineer-option">
                  <input
                    type="radio"
                    name="engineer_status"
                    checked={showInactiveEngineers}
                    onChange={() =>
                      setShowInactiveEngineers(true)
                    }
                  />

                  Active + Inactive
                </label>

              </div>
            </div>


            {/* REPORT VERSION */}
            {canUseAdvancedReport && (
              <div className="admin-report-version">

                <label className="admin-filter-heading">
                  Report Version
                </label>

                <select
                  value={reportVersion}
                  onChange={(e) =>
                    setReportVersion(e.target.value)
                  }
                >
                  <option value="standard">
                    Standard Report
                  </option>

                  <option value="payroll">
                    Payroll Report + Summary
                  </option>
                </select>

              </div>
            )}

          </div>


          {/* SEARCH FILTERS */}
          <TimesheetSearch
            initialConfig={normalUserfilterConfig}
            onSearch={handleSearch}
            onReportGen={handleReportGen}
            presetFilters={searchPreset}
          />

        </section>

        <div className="admin-stats-grid">

          {/* TOTAL TIMESHEETS */}
          <div className="admin-stat-card admin-stat-neutral">

            <div className="admin-stat-icon">
              <i className="fa fa-file-text-o"></i>
            </div>

            <div className="admin-stat-content">
              <p className="admin-stat-label">
                Total Time Sheets
              </p>

              <p className="admin-stat-value">
                {jobSummary
                  ? jobSummary.total_timesheets
                  : totalCount || 0}
              </p>
            </div>

          </div>


          {/* JOB TOTAL */}
          <div className="admin-stat-card admin-stat-blue">

            <div className="admin-stat-icon">
              <i className="fa fa-briefcase"></i>
            </div>

            <div className="admin-stat-content">

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <p className="admin-stat-label">
                  {jobSummary?.job_number
                    ? `Job ${jobSummary.job_number} Total`
                    : "Job Total"}
                </p>

                {jobSummary?.job_number && (
                  <button
                    type="button"
                    title="Copy Job Number"
                    className={
                      jobCopied
                        ? "admin-copy-button copied"
                        : "admin-copy-button"
                    }
                    onClick={async () => {
                      const copied = await copyToClipboard(
                        String(jobSummary.job_number)
                      );

                      if (copied) {
                        setJobCopied(true);

                        setTimeout(() => {
                          setJobCopied(false);
                        }, 1500);
                      }
                    }}
                  >
                    {jobCopied ? (
                      <>
                        <i className="fa fa-check"></i>{" "}
                        Copied!
                      </>
                    ) : (
                      <i className="fa fa-copy"></i>
                    )}
                  </button>
                )}
              </div>

              <p className="admin-stat-value">
                {jobSummary
                  ? Number(
                      jobSummary.total_amount || 0
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "-"}
              </p>

            </div>
          </div>


          {/* COMPLETED */}
          <div className="admin-stat-card admin-stat-green">

            <div className="admin-stat-icon">
              <i className="fa fa-check-circle"></i>
            </div>

            <div className="admin-stat-content">

              <p className="admin-stat-label">
                Completed Amount
              </p>

              <p className="admin-stat-value">
                {jobSummary
                  ? Number(
                      jobSummary.completed_amount || 0
                    ).toFixed(2)
                  : "-"}
              </p>

            </div>
          </div>


          {/* PENDING */}
          <div className="admin-stat-card admin-stat-amber">

            <div className="admin-stat-icon">
              <i className="fa fa-clock-o"></i>
            </div>

            <div className="admin-stat-content">

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <p className="admin-stat-label">
                  Pending Amount
                </p>

                {jobSummary && (
                  <button
                    type="button"
                    title="Copy Pending Amount"
                    className={
                      pendingCopied
                        ? "admin-copy-button copied"
                        : "admin-copy-button"
                    }
                    onClick={async () => {
                      const pendingAmount = Number(
                        jobSummary.pending_amount || 0
                      ).toFixed(2);

                      const copied = await copyToClipboard(
                        pendingAmount
                      );

                      if (copied) {
                        setPendingCopied(true);

                        setTimeout(() => {
                          setPendingCopied(false);
                        }, 1500);
                      }
                    }}
                  >
                    {pendingCopied ? (
                      <>
                        <i className="fa fa-check"></i>{" "}
                        Copied!
                      </>
                    ) : (
                      <i className="fa fa-copy"></i>
                    )}
                  </button>
                )}
              </div>

              <p className="admin-stat-value">
                {jobSummary
                  ? Number(
                      jobSummary.pending_amount || 0
                    ).toFixed(2)
                  : "-"}
              </p>

            </div>
          </div>

        </div>

    <div className="admin-timesheet-card">
      <div className="admin-timesheet-scroll">
        <table
          className="table table-striped admin-timesheet-table"
          style={{
            marginBottom: 0,
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <thead>
            <tr
              style={{
                fontSize: 12,
                textAlign: "center",
                verticalAlign: "middle",
              }}
            >
              <th style={columnStyles.view}>View</th>
              <th style={columnStyles.date}>
                Date
                <br />
                y-m-d
              </th>
              <th style={columnStyles.jobNo}>
              Job
              <br />
              No
            </th>
            <th style={columnStyles.database}>
              Database
            </th>
            <th style={columnStyles.vessel}>
              Vessel Name
            </th>
              <th style={columnStyles.engineer}>
                Eng
                <br />
                Name
              </th>
              <th style={columnStyles.time}>
                Start
                <br />
                Time
              </th>
              <th style={columnStyles.time}>
                Sign
                <br />
                In
              </th>
              <th style={columnStyles.time}>
                Sign
                <br />
                Out
              </th>
              <th style={columnStyles.time}>Return</th>
              <th style={columnStyles.normalHours}>
                Normal
                <br />
                Hrs
              </th>
              <th style={columnStyles.hours}>
                Total
                <br />
                Hrs
              </th>
              <th style={columnStyles.hours}>
                OT
                <br />
                Hrs
              </th>
              <th style={columnStyles.amountSmall}>
                Exp.
                <br />
                OT
              </th>
              <th style={columnStyles.amountSmall}>
                OT
                <br />
                Amt
              </th>
              <th style={columnStyles.amount}>Alw.Amt</th>
              <th style={stickyTotalHeaderStyle}>Total</th>
              <th style={columnStyles.flag}>H.day</th>
              <th style={columnStyles.flag}>Overseas</th>
              {/* <th style={columnStyles.flag}>OS</th>
              <th style={columnStyles.flag}>DryD</th> */}
              <th style={stickyProcessHeaderStyle}>
                Process
                <br />
                Status
              </th>
              <th style={columnStyles.processedBy}>
                Processed
                <br />
                By
              </th>
              <th style={columnStyles.processedAt}>
                Processed
                <br />
                At
              </th>
              <th style={columnStyles.comments}>Comments</th>
            </tr>
          </thead>

          <tbody>
            {timesheetsData?.map((timesheet) => {
              const weekendDay = getWeekendDay(timesheet.timesheet_date);

              return (
                <tr
                  key={timesheet.timesheet_id}
                  style={{ fontSize: 13 }}
                  className={
                    timesheet.timesheet_final_process_status
                      ? "table-success"
                      : ""
                  }
                >
                  {/* View cell remains a normal table-cell; flex is inside it. */}
                  <td style={{ ...commonCellStyle, ...columnStyles.view }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        width: "100%",
                      }}
                    >
                      <button
                        type="button"
                        className="btn fa fa-eye"
                        title="View Timesheet"
                        style={{
                          color: "blue",
                          padding: 0,
                          margin: 0,
                          width: 26,
                          height: 26,
                        }}
                        onClick={() => handleViewClick(timesheet)}
                      ></button>

                      {timesheet.timesheet_attendance_id
                        ?.attendance_sr_upload_location ? (
                        <a
                          href={
                            timesheet.timesheet_attendance_id
                              .attendance_sr_upload_location
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn"
                          title="View Report"
                          style={{
                            color: "blue",
                            padding: 0,
                            margin: 0,
                            width: 26,
                            height: 26,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i className="fa-solid fa-file"></i>
                        </a>
                      ) : (
                        <span
                          aria-hidden="true"
                          style={{ width: 26, height: 26, display: "block" }}
                        ></span>
                      )}
                    </div>
                  </td>

                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.date,
                      fontSize: 11,
                    }}
                  >
                    {timesheet.timesheet_date || "-"}

                    {weekendDay && (
                      <>
                        <br />
                        <span
                          style={{
                            color: "red",
                            fontWeight: "bold",
                            fontSize: 11,
                          }}
                        >
                          {weekendDay}
                        </span>
                      </>
                    )}
                  </td>
                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.jobNo,
                      fontSize: 11,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <span>
                        {timesheet.timesheet_work_id?.work_job_number || "-"}
                      </span>

                      {timesheet.timesheet_work_id?.work_job_number && (
                        <button
                          type="button"
                          title="Search this Job Number"
                          onClick={() =>
                            handleJobQuickSearch(
                              timesheet.timesheet_work_id.work_job_number
                            )
                          }
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#007bff",
                            cursor: "pointer",
                            padding: "2px",
                            fontSize: "12px",
                          }}
                        >
                          <i className="fa fa-search"></i>
                        </button>
                      )}
                    </div>
                  </td>

                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.database,
                      fontSize: 11,
                    }}
                  >
                    {timesheet.timesheet_work_id?.branch_name || "-"}
                  </td>

                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.vessel,
                      fontSize: 11,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={timesheet.timesheet_work_id?.work_vessel || ""}
                  >
                    {timesheet.timesheet_work_id?.work_vessel || "-"}
                  </td>
                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.engineer,
                      fontSize: 11,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={timesheet.emp_name || ""}
                  >
                    {timesheet.emp_name || "-"}
                  </td>

                  <td style={{ ...commonCellStyle, ...columnStyles.time }}>
                    {timesheet.timesheet_time_start || "-"}
                  </td>
                  <td style={{ ...commonCellStyle, ...columnStyles.time }}>
                    {timesheet.timesheet_time_signin || "-"}
                  </td>
                  <td style={{ ...commonCellStyle, ...columnStyles.time }}>
                    {timesheet.timesheet_time_signout || "-"}
                  </td>
                  <td style={{ ...commonCellStyle, ...columnStyles.time }}>
                    {timesheet.timesheet_time_return || "-"}
                  </td>
                  <td
                    style={{ ...commonCellStyle, ...columnStyles.normalHours }}
                  >
                    {decimalToTime(timesheet.timesheet_normal_hours)}
                  </td>
                  <td style={{ ...commonCellStyle, ...columnStyles.hours }}>
                    {decimalToTime(timesheet.timesheet_total_hours)}
                  </td>
                  <td style={{ ...commonCellStyle, ...columnStyles.hours }}>
                    {decimalToTime(timesheet.timesheet_over_time_hours)}
                  </td>
                  <td
                    style={{ ...commonCellStyle, ...columnStyles.amountSmall }}
                  >
                    {timesheet.timesheet_expected_amount ?? "-"}
                  </td>
                  <td
                    style={{ ...commonCellStyle, ...columnStyles.amountSmall }}
                  >
                    {timesheet.timesheet_overtime_amount ?? "-"}
                  </td>
                  <td style={{ ...commonCellStyle, ...columnStyles.amount }}>
                    {timesheet.timesheet_allowance_amount ?? "-"}
                  </td>
                  <td
                    style={{
                      ...stickyTotalCellStyle,
                      fontWeight: "bold",
                    }}
                  >
                    {timesheet.timesheet_Total_amount ?? "-"}
                  </td>

                  <td style={{ ...commonCellStyle, ...columnStyles.flag }}>
                    {timesheet.timesheet_is_holiday ? (
                      <span className="fa fa-check badge text-white bg-success">
                        &nbsp;
                      </span>
                    ) : (
                      <span className="fa fa-times badge text-white bg-secondary">
                        &nbsp;
                      </span>
                    )}
                  </td>

                  <td style={{ ...commonCellStyle, ...columnStyles.flag }}>
                    {timesheet.timesheet_attendance_id
                      ?.attendance_is_anchorage ? (
                      <span className="fa fa-check badge text-white bg-success">
                        &nbsp;
                      </span>
                    ) : (
                      <span className="fa fa-times badge text-white bg-secondary">
                        &nbsp;
                      </span>
                    )}
                  </td>

                  {/* <td style={{ ...commonCellStyle, ...columnStyles.flag }}>
                    {timesheet.timesheet_attendance_id
                      ?.attendance_is_overseas ? (
                      <span className="fa fa-check badge text-white bg-success">
                        &nbsp;
                      </span>
                    ) : (
                      <span className="fa fa-times badge text-white bg-secondary">
                        &nbsp;
                      </span>
                    )}
                  </td>

                  <td style={{ ...commonCellStyle, ...columnStyles.flag }}>
                    {timesheet.timesheet_attendance_id
                      ?.attendance_is_dubaidrydock ? (
                      <span className="fa fa-check badge text-white bg-success">
                        &nbsp;
                      </span>
                    ) : (
                      <span className="fa fa-times badge text-white bg-secondary">
                        &nbsp;
                      </span>
                    )}
                  </td> */}

                  <td style={stickyProcessCellStyle}>
                    {!timesheet.timesheet_final_process_status ? (
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={processStatusButtonStyle}
                        onClick={() => handleProcessClick(timesheet)}
                        disabled={
                          !timesheet.timesheet_total_hours &&
                          timesheet.timesheet_total_hours !== 0
                        }
                      >
                        Waiting
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-success"
                        style={processStatusButtonStyle}
                        onClick={() => handleProcessClick(timesheet)}
                        disabled={
                          !timesheet.timesheet_total_hours &&
                          timesheet.timesheet_total_hours !== 0.0
                        }
                      >
                        Completed
                      </button>
                    )}
                  </td>

                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.processedBy,
                      fontSize: 11,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={timesheet.timesheet_processed_by_name || ""}
                  >
                    {timesheet.timesheet_processed_by_name || "-"}
                  </td>

                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.processedAt,
                      fontSize: 11,
                      whiteSpace: "normal",
                    }}
                  >
                    {formatDateTime(timesheet.timesheet_processed_at)}
                  </td>

                  <td
                    style={{
                      ...commonCellStyle,
                      ...columnStyles.comments,
                      fontSize: 11,
                      textAlign: "center",
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                    }}
                  >
                    {timesheet.timesheet_comments || "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onNextPage={() => handlePageChange(currentPage + 1)}
        onPrevPage={() => handlePageChange(currentPage - 1)}
      />

      {selectedTimesheet && (
        <DetailedUserView
          timesheet={selectedTimesheet}
          onUpdate={handleSaveEdit}
          onClosePopup={handleCloseEdit}
        />
      )}
    </div>
  );
};

export default AdminApprovalControl;